import axios from 'axios';
import { API_BASE_URL, handleApiError } from './_apiBase';

/** PÅ eller AV vid ett tillfälle. */
export type ScheduleAction = 'ON' | 'OFF';

export interface DeviceScheduleDevice {
    device_id: number;
    /** 0 = hela enheten, annars utgången på en IO-modul. */
    io_endpoint: number;
    name?: string;
    type?: string;
    disabled?: boolean;
}

/** Ett planerat tillfälle. run_at är en ISO-tid (UTC). */
export interface DeviceScheduleRunPlan {
    id?: number;
    run_at: string;
    action: ScheduleAction;
}

export type RunStatus = 'planned' | 'running' | 'ok' | 'failed' | 'missed' | 'cancelled';

export interface DeviceScheduleRunItem {
    device_id: number;
    io_endpoint: number;
    device_name: string;
    result: 'ok' | 'failed' | 'skipped';
    power_value: number | null;
    message: string | null;
    attempts: number;
    checked_at: string;
}

export interface DeviceScheduleRun {
    id: number;
    run_at: string;
    action: ScheduleAction;
    status: RunStatus;
    started_at: string | null;
    finished_at: string | null;
    summary: string | null;
    ok_notified_at?: string | null;
    items?: DeviceScheduleRunItem[];
}

export interface DeviceSchedule {
    id: number;
    name: string;
    /** Larmas om någon enhet inte hamnar i rätt läge. Krav. */
    fail_group_id: number | null;
    fail_group_name: string | null;
    fail_group_members: number;
    /** Får ett besked när alla enheter är i rätt läge. Valfritt. */
    ok_group_id: number | null;
    ok_group_name: string | null;
    power_check: boolean;
    power_property: string;
    power_min_watts: number | null;
    power_delay_seconds: number;
    alert_rule_id: number | null;
    rule_enabled: boolean | null;
    active: boolean;
    devices: DeviceScheduleDevice[];
    runs: DeviceScheduleRunPlan[];
    last_run: DeviceScheduleRun | null;
}

export type DeviceScheduleInput = Pick<DeviceSchedule,
    'name' | 'fail_group_id' | 'ok_group_id' | 'power_check' | 'power_property' | 'power_min_watts' | 'power_delay_seconds' | 'active'> & {
    devices: { device_id: number; io_endpoint: number }[];
    runs: DeviceScheduleRunPlan[];
};

/** En enhet som går att slå på och av. */
export interface SwitchableDevice {
    id: number;
    name: string;
    type: string;
    disabled: boolean;
    has_power: boolean;
    zones: string[];
    io_endpoints: number[];
}

const base = `${API_BASE_URL}/device-schedules`;

export const fetchDeviceSchedules = async (): Promise<DeviceSchedule[]> => {
    try { return (await axios.get<DeviceSchedule[]>(base)).data; }
    catch (error) { return handleApiError(error, 'Hämta scheman'); }
};

export const fetchSwitchableDevices = async (): Promise<SwitchableDevice[]> => {
    try { return (await axios.get<SwitchableDevice[]>(`${base}/devices`)).data; }
    catch (error) { return handleApiError(error, 'Hämta enheter'); }
};

export const createDeviceSchedule = async (body: DeviceScheduleInput): Promise<DeviceSchedule> => {
    try { return (await axios.post<DeviceSchedule>(base, body)).data; }
    catch (error) { return handleApiError(error, 'Spara schema'); }
};

export const updateDeviceSchedule = async (id: number, body: DeviceScheduleInput): Promise<DeviceSchedule> => {
    try { return (await axios.put<DeviceSchedule>(`${base}/${id}`, body)).data; }
    catch (error) { return handleApiError(error, 'Spara schema'); }
};

export const deleteDeviceSchedule = async (id: number): Promise<void> => {
    try { await axios.delete(`${base}/${id}`); }
    catch (error) { return handleApiError(error, 'Ta bort schema'); }
};

export const fetchDeviceScheduleRuns = async (id: number): Promise<DeviceScheduleRun[]> => {
    try { return (await axios.get<DeviceScheduleRun[]>(`${base}/${id}/runs`)).data; }
    catch (error) { return handleApiError(error, 'Hämta körningar'); }
};

export const runDeviceScheduleNow = async (id: number, action: ScheduleAction): Promise<{ run_id: number }> => {
    try { return (await axios.post<{ run_id: number }>(`${base}/${id}/run-now`, { action })).data; }
    catch (error) { return handleApiError(error, 'Kör schemat'); }
};

export const recheckDeviceScheduleRun = async (runId: number): Promise<DeviceScheduleRun> => {
    try { return (await axios.post<DeviceScheduleRun>(`${base}/runs/${runId}/recheck`)).data; }
    catch (error) { return handleApiError(error, 'Kontrollera igen'); }
};
